/**
 * ListActionsMenu — wspólne menu AKCJI listy (PHASE 25 Faza 1), zastępuje dawne
 * „Operacje". Jedno źródło pozycji, trzy wejścia:
 *  - `ListActionsMenu` — dropdown „Akcje ▾" nad listą (ten plik),
 *  - menu prawego kliknięcia na zaznaczonych rekordach (reużywa `ActionMenuItems`),
 *  - kebab „⋯" w wierszu (reużywa `ActionMenuItems`).
 *
 * Pozycje przychodzą już wyliczone z `resolveListActions` (katalog ∩ kartoteka ∩
 * moduły ∩ uprawnienia) w dwóch grupach: „na zaznaczonych" i „ogólne". Akcje na
 * zaznaczeniu są nieaktywne, gdy nic nie zaznaczone — nie chowamy ich, tylko
 * wyszarzamy z podpowiedzią (decyzja UX: user ma wiedzieć, że istnieją).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListActionDef, ListActionId } from '@saas/shared';
import {
  Archive,
  ArchiveRestore,
  Bolt,
  Calculator,
  Check,
  ChevronDown,
  FileDown,
  FileSpreadsheet,
  Landmark,
  type LucideIcon,
  Pencil,
  RefreshCw,
  Send,
  Trash2,
  Upload,
} from 'lucide-react';

import { renderIconByName } from '@/components/modules/IconPicker';

/** Mapowanie nazw ikon z katalogu (`list-actions.ts`) na komponenty lucide. */
const ACTION_ICONS: Record<string, LucideIcon> = {
  archive: Archive,
  restore: ArchiveRestore,
  trash: Trash2,
  'file-export': FileDown,
  'file-spreadsheet': FileSpreadsheet,
  upload: Upload,
  refresh: RefreshCw,
  edit: Pencil,
  send: Send,
  'building-bank': Landmark,
  calculator: Calculator,
};

/** Ikona lucide dla akcji katalogu (fallback: `Bolt`). Reużywalne poza tym menu. */
export function listActionLucideIcon(iconName: string): LucideIcon {
  return ACTION_ICONS[iconName] ?? Bolt;
}

export interface ListActionsMenuProps {
  /** Akcje na zaznaczonych (aktywne tylko gdy `selectedCount > 0`). */
  selectionActions: ListActionDef[];
  /** Akcje ogólne (zawsze aktywne). */
  generalActions: ListActionDef[];
  /** Liczba zaznaczonych rekordów (steruje aktywnością sekcji „na zaznaczonych"). */
  selectedCount: number;
  /** Wywołanie akcji po kliknięciu pozycji. */
  onRun: (actionId: ListActionId) => void;
  /**
   * Akcje dostępne w katalogu, ale jeszcze niepodpięte na tej liście (brak handlera /
   * endpointu) — renderowane wyszarzone z badge „wkrótce", żeby funkcja nie umknęła
   * (decyzja Eugenii). Np. eksport/import na listach bez własnego endpointu.
   */
  unavailableActions?: Set<ListActionId>;
  /**
   * Akcje zbramkowane brakiem konfiguracji — renderowane jako AKTYWNE (klikalne), ale z
   * chipem „Konfiguracja". Klik nie wykonuje akcji (obsługuje to `onRun` → hook, który
   * otwiera modal naprowadzający). Różne od `unavailableActions` („wkrótce", wyszarzone).
   */
  gatedActions?: Set<ListActionId>;
  /** Akcje WŁASNE tenanta dla tej kartoteki (T25F2.6) — renderowane pod wbudowanymi „na zaznaczonych". */
  customActions?: CustomActionMenuItem[];
  /** Wykonanie akcji własnej po kliknięciu. */
  onRunCustom?: (id: string) => void;
  /**
   * Przełącznik „Pokaż zarchiwizowane" — renderowany jako pozycja menu (sekcja ogólna),
   * gdy podany. Klik przełącza widok aktywne↔zarchiwizowane. Brak = bez tej pozycji.
   */
  archivedToggle?: { active: boolean; onToggle: () => void };
  /**
   * Własne przyciski z Kreatora Listy (zakładka „Przyciski"): `bulkButtons` = pozycja
   * „Akcje masowe" (na zaznaczonych), `rowButtons` = „Akcja wiersza" (aktywne przy
   * dokładnie jednym zaznaczonym rekordzie, np. prawy klik na wierszu).
   */
  bulkButtons?: CustomButtonMenuItem[];
  rowButtons?: CustomButtonMenuItem[];
  onRunButton?: (id: string) => void;
  /** Przyciski z pozycją „Pasek narzędzi" — renderowane obok dropdowna „Akcje". */
  toolbar?: React.ReactNode;
  testIdPrefix?: string;
  /** Nadpisanie klas przycisku-triggera (np. `h-10 !rounded-full` w nagłówku kartoteki). */
  triggerClassName?: string;
}

/** Lekki kształt akcji własnej dla menu (structural — bez importu z useListActions, żeby uniknąć cyklu). */
export interface CustomActionMenuItem {
  id: string;
  name: string;
  icon: string;
  requireConfirm: boolean;
}

/** Własny przycisk z Kreatora Listy jako pozycja menu. */
export interface CustomButtonMenuItem {
  id: string;
  label: string;
  icon?: string;
}

/**
 * ActionMenuItems — sama treść menu (dwie sekcje). Reużywalna: dropdown, menu
 * kontekstowe (prawy klik) i kebab wiersza renderują TO SAMO.
 */
export function ActionMenuItems({
  selectionActions,
  generalActions,
  selectedCount,
  onRun,
  unavailableActions,
  gatedActions,
  customActions,
  onRunCustom,
  archivedToggle,
  bulkButtons,
  rowButtons,
  onRunButton,
  onAfterRun,
  testIdPrefix = 'list',
}: ListActionsMenuProps & { onAfterRun?: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const hasSelection = selectedCount > 0;
  const isSoon = (id: ListActionId) => unavailableActions?.has(id) ?? false;
  const isGated = (id: ListActionId) => gatedActions?.has(id) ?? false;
  const custom = customActions ?? [];
  const bulk = bulkButtons ?? [];
  const row = rowButtons ?? [];
  const hasSelectionSection =
    selectionActions.length > 0 || custom.length > 0 || bulk.length > 0 || row.length > 0;
  const hasAnyAbove = hasSelectionSection || generalActions.length > 0;
  const buttonRow = (b: CustomButtonMenuItem, disabled: boolean) => (
    <ActionRow
      key={`btn-${b.id}`}
      rawLabel={b.label}
      rawIcon={b.icon}
      disabled={disabled}
      onClick={() => {
        onRunButton?.(b.id);
        onAfterRun?.();
      }}
      testId={`${testIdPrefix}-button-${b.id}`}
    />
  );

  return (
    <>
      {hasSelectionSection && (
        <>
          <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            {hasSelection
              ? t('tenant.listActions.group.selectedCount', { count: selectedCount })
              : t('tenant.listActions.group.selected')}
          </p>
          {!hasSelection && (
            <p
              className="px-3 pb-1 text-[11px] text-text-muted"
              data-testid={`${testIdPrefix}-actions-selection-hint`}
            >
              {t('tenant.listActions.selectHint')}
            </p>
          )}
          {selectionActions.map((def) => (
            <ActionRow
              key={def.id}
              def={def}
              disabled={!hasSelection || isSoon(def.id)}
              soon={isSoon(def.id)}
              gated={isGated(def.id)}
              onClick={() => {
                onRun(def.id);
                onAfterRun?.();
              }}
              testId={`${testIdPrefix}-action-${def.id}`}
            />
          ))}
          {custom.map((c) => (
            <ActionRow
              key={c.id}
              rawLabel={c.name}
              rawIcon={c.icon}
              disabled={!hasSelection}
              onClick={() => {
                onRunCustom?.(c.id);
                onAfterRun?.();
              }}
              testId={`${testIdPrefix}-custom-${c.id}`}
            />
          ))}
          {bulk.map((b) => buttonRow(b, !hasSelection))}
          {row.map((b) => buttonRow(b, selectedCount !== 1))}
        </>
      )}

      {selectionActions.length > 0 && generalActions.length > 0 && (
        <div className="my-1 border-t border-border-secondary" />
      )}

      {generalActions.length > 0 && (
        <>
          <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            {t('tenant.listActions.group.general')}
          </p>
          {generalActions.map((def) => (
            <ActionRow
              key={def.id}
              def={def}
              disabled={isSoon(def.id)}
              soon={isSoon(def.id)}
              gated={isGated(def.id)}
              onClick={() => {
                onRun(def.id);
                onAfterRun?.();
              }}
              testId={`${testIdPrefix}-action-${def.id}`}
            />
          ))}
        </>
      )}

      {archivedToggle && (
        <>
          {hasAnyAbove && <div className="my-1 border-t border-border-secondary" />}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={archivedToggle.active}
            onClick={() => {
              archivedToggle.onToggle();
              onAfterRun?.();
            }}
            data-testid={`${testIdPrefix}-action-toggle-archived`}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
              archivedToggle.active
                ? 'text-accent-700 hover:bg-bg-secondary dark:text-accent-300'
                : 'text-text-primary hover:bg-bg-secondary'
            }`}
          >
            {archivedToggle.active ? (
              <ArchiveRestore size={14} aria-hidden="true" className="shrink-0" />
            ) : (
              <Archive size={14} aria-hidden="true" className="shrink-0" />
            )}
            <span className="flex-1">
              {archivedToggle.active
                ? t('tenant.listFilterBar.showArchivedActive')
                : t('tenant.listFilterBar.showArchived')}
            </span>
            {archivedToggle.active && <Check size={14} aria-hidden="true" className="shrink-0" />}
          </button>
        </>
      )}
    </>
  );
}

export function ListActionsMenu(props: ListActionsMenuProps): React.ReactElement | null {
  const { t } = useTranslation();
  const {
    selectionActions,
    generalActions,
    archivedToggle,
    customActions,
    bulkButtons,
    rowButtons,
    toolbar,
    testIdPrefix = 'list',
    triggerClassName,
  } = props;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // Nic do pokazania w menu (np. layout z pustą listą akcji) — bez przycisku „Akcje";
  // własne przyciski paska zostają.
  const hasMenu =
    selectionActions.length > 0 ||
    generalActions.length > 0 ||
    (customActions?.length ?? 0) > 0 ||
    (bulkButtons?.length ?? 0) > 0 ||
    (rowButtons?.length ?? 0) > 0 ||
    !!archivedToggle;
  if (!hasMenu) return toolbar ? <>{toolbar}</> : null;
  return (
    <>
      {toolbar}
      <div className="relative" ref={rootRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className={`inline-flex h-11 items-center gap-2 rounded-full border border-border-secondary bg-bg-elevated pl-4 pr-5 text-sm text-text-primary shadow-[0_1px_1px_rgba(23,23,33,0.06)] transition-colors hover:bg-bg-tertiary ${triggerClassName ?? ''}`}
          data-testid={`${testIdPrefix}-actions-btn`}
        >
          <Bolt size={16} aria-hidden="true" />
          {t('tenant.listActions.menuButton')}
          <ChevronDown size={14} aria-hidden="true" />
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 z-50 mt-1 min-w-[240px] rounded-lg border border-border-secondary bg-bg-elevated py-1 shadow-lg"
            data-testid={`${testIdPrefix}-actions-menu`}
          >
            <ActionMenuItems {...props} onAfterRun={() => setOpen(false)} />
          </div>
        )}
      </div>
    </>
  );
}

function ActionRow({
  def,
  rawLabel,
  rawIcon,
  disabled,
  soon,
  gated,
  onClick,
  testId,
}: {
  /** Akcja wbudowana z katalogu; pomiń dla akcji WŁASNej (użyj rawLabel/rawIcon). */
  def?: ListActionDef;
  /** Etykieta akcji WŁASNej (surowa nazwa, nie i18n). */
  rawLabel?: string;
  /** Nazwa ikony Lucide akcji WŁASNej (renderowana dynamicznie). */
  rawIcon?: string;
  disabled: boolean;
  soon?: boolean;
  gated?: boolean;
  onClick: () => void;
  testId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const Icon = def ? (ACTION_ICONS[def.icon] ?? Bolt) : null;
  const label = rawLabel ?? (def ? t(def.labelKey) : '');
  const destructive = def?.destructive ?? false;
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onClick();
      }}
      data-testid={testId}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
        disabled
          ? 'cursor-not-allowed text-text-muted opacity-60'
          : destructive
            ? 'text-text-danger hover:bg-bg-secondary'
            : 'text-text-primary hover:bg-bg-secondary'
      }`}
    >
      {Icon ? (
        <Icon size={14} aria-hidden="true" className="shrink-0" />
      ) : (
        <span
          className="flex h-[14px] w-[14px] shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          {renderIconByName(rawIcon ?? 'Zap', 'h-3.5 w-3.5')}
        </span>
      )}
      <span className="flex-1">{label}</span>
      {soon && (
        <span className="rounded bg-bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-text-muted">
          {t('common.soon')}
        </span>
      )}
      {!soon && gated && (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          {t('tenant.listActions.configRequired')}
        </span>
      )}
    </button>
  );
}
